import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadSubmissionImage } from '../lib/queries';
import { useToast } from './Toast';
import type { Submission } from '../lib/types';
import './SubmissionComposer.css';

interface Props {
  /** Viewer's auth id — used to key the storage upload path. */
  userId: string;
  /** When editing, the member's current submission (pre-fills image + body). */
  existing?: Submission | null;
  /** Persist: receives the resolved image URL + body. Resolves when saved. */
  onSave: (args: { imageUrl: string; body: string }) => Promise<void>;
  /** Close without saving (× / Esc / backdrop / Cancel). */
  onClose: () => void;
}

const MAX_BODY = 600;

/**
 * Modal composer for a member's single Redesign Challenge submission. Uploads
 * one image (uploadSubmissionImage) and writes a description, then hands the
 * resolved public URL + body to onSave (which calls upsertSubmission upstream).
 * In edit mode it pre-fills from `existing`; the image is only re-uploaded if
 * the member picks a new file.
 */
export function SubmissionComposer({ userId, existing, onSave, onClose }: Props) {
  const toast = useToast();
  const overlayRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const [body, setBody] = useState(existing?.body ?? '');
  // Local object-URL preview when a new file is chosen; falls back to the
  // existing submission's image when editing and no new file is picked.
  const [previewUrl, setPreviewUrl] = useState<string | null>(existing?.image_url ?? null);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const isEdit = !!existing;

  // Body scroll lock + initial focus.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Revoke object URLs we create to avoid leaks.
  useEffect(() => {
    return () => {
      if (pickedFile && previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [pickedFile, previewUrl]);

  const close = useCallback(() => {
    if (saving) return;
    onClose();
  }, [saving, onClose]);

  // Esc to close, Tab trapped within the dialog.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') {
        const root = overlayRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPickedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }

  function onBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) close();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    // Must have either a freshly-picked file or an existing image to keep.
    if (!pickedFile && !existing?.image_url) {
      toast.error('Add an image to submit your design.');
      return;
    }
    setSaving(true);
    try {
      let imageUrl = existing?.image_url ?? '';
      if (pickedFile) {
        imageUrl = await uploadSubmissionImage({ userId, file: pickedFile });
      }
      await onSave({ imageUrl, body: body.trim() });
    } catch (err) {
      console.error('[submission-composer] save failed', err);
      toast.error('Could not save your submission. Try again.');
      setSaving(false);
    }
  }

  return (
    <div
      ref={overlayRef}
      className="subcomposer"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit your design' : 'Submit your design'}
      onClick={onBackdropClick}
    >
      <form className="subcomposer__panel" onSubmit={submit}>
        <header className="subcomposer__head">
          <h3 className="subcomposer__title">{isEdit ? 'Edit your design' : 'Submit your design'}</h3>
          <button
            ref={closeRef}
            type="button"
            className="subcomposer__close"
            onClick={close}
            aria-label="Close"
            disabled={saving}
          >
            ×
          </button>
        </header>

        <div className="subcomposer__body">
          <button
            type="button"
            className={`subcomposer__drop ${previewUrl ? 'subcomposer__drop--has' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            aria-label={previewUrl ? 'Replace image' : 'Add image'}
          >
            {previewUrl ? (
              <>
                <img className="subcomposer__preview" src={previewUrl} alt="Your design preview" />
                <span className="subcomposer__replace">Replace image</span>
              </>
            ) : (
              <span className="subcomposer__drop-hint">
                <span className="subcomposer__drop-plus" aria-hidden="true">+</span>
                Add your design image
              </span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="subcomposer__file"
            onChange={onPickFile}
          />

          <label className="subcomposer__label" htmlFor="subcomposer-body">
            Description
          </label>
          <textarea
            id="subcomposer-body"
            className="subcomposer__textarea"
            placeholder="Tell us about your redesign…"
            value={body}
            maxLength={MAX_BODY}
            onChange={(e) => setBody(e.target.value)}
          />
          <span className="subcomposer__count">{body.length}/{MAX_BODY}</span>
        </div>

        <footer className="subcomposer__foot">
          <button type="button" className="btn btn--secondary btn--sm" onClick={close} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary btn--sm"
            disabled={saving || (!pickedFile && !existing?.image_url)}
          >
            {saving ? (isEdit ? 'Saving…' : 'Submitting…') : isEdit ? 'Save changes' : 'Submit design'}
          </button>
        </footer>
      </form>
    </div>
  );
}
